/**
 * Operações básicas que alteram o estado: pagar, receber, mover, prender, construir.
 * Tudo aqui age sobre `ctx.s` (o rascunho do estado) e registra eventos em `ctx.ev`.
 */
import { CITY_SQUARES, isOwnable, square } from './board';
import { getPlayer, ownsFullGroup, turnsPerRound } from './queries';
import { BOARD_SIZE, HAND_LIMIT, ISLAND_SQUARE, PASS_START_BONUS } from './rules';
import type { Creditor, Effect, GameEvent, GameState, HeldCard, Level, Player, PlayerId } from './types';

export interface Ctx {
  s: GameState;
  ev: GameEvent[];
}

export function emit(ctx: Ctx, e: GameEvent): void {
  ctx.ev.push(e);
}

/** Banco paga ao jogador. */
export function credit(ctx: Ctx, player: PlayerId, amount: number, reason: string): void {
  if (amount <= 0) return;
  getPlayer(ctx.s, player).cash += amount;
  emit(ctx, { type: 'received', player, amount, reason });
}

/** Move dinheiro de um jogador para outro jogador ou para o banco. O pagador precisa ter o valor. */
export function transfer(ctx: Ctx, from: PlayerId, to: Creditor, amount: number, reason: string): void {
  if (amount <= 0) return;
  const payer = getPlayer(ctx.s, from);
  if (payer.cash < amount) throw new Error(`transfer sem saldo: ${from} ${amount}`);
  payer.cash -= amount;
  if (to !== 'bank') {
    const receiver = getPlayer(ctx.s, to);
    receiver.cash += amount;
    if (reason === 'aluguel') receiver.stats.rentReceived += amount;
  }
  if (reason === 'aluguel') payer.stats.rentPaid += amount;
}

/**
 * Cobra um valor. Se o devedor tem o dinheiro, paga na hora; senão, abre uma dívida
 * que trava a partida até ele vender propriedades ou quebrar.
 */
export function charge(
  ctx: Ctx,
  debtor: PlayerId,
  creditor: Creditor,
  amount: number,
  reason: string,
  silent = false,
): void {
  if (amount <= 0 || debtor === creditor) return;
  const p = getPlayer(ctx.s, debtor);
  if (p.eliminated) return;
  if (creditor !== 'bank' && getPlayer(ctx.s, creditor).eliminated) return;
  if (p.cash >= amount) {
    transfer(ctx, debtor, creditor, amount, reason);
    if (!silent) emit(ctx, { type: 'paid', from: debtor, to: creditor, amount, reason });
    return;
  }
  ctx.s.debts.push({ debtor, creditor, amount, reason });
  emit(ctx, { type: 'debtOpened', debtor, creditor, amount, reason });
}

/** Anda para frente; ao passar ou parar no Início, recebe o salário. */
export function moveForward(ctx: Ctx, player: PlayerId, steps: number): void {
  const p = getPlayer(ctx.s, player);
  const from = p.position;
  const raw = from + steps;
  p.position = ((raw % BOARD_SIZE) + BOARD_SIZE) % BOARD_SIZE;
  emit(ctx, { type: 'moved', player, from, to: p.position, how: 'walk' });
  if (raw >= BOARD_SIZE) {
    p.laps += 1;
    credit(ctx, player, PASS_START_BONUS, 'inicio');
    emit(ctx, { type: 'passedStart', player, amount: PASS_START_BONUS });
  }
}

/** Anda para frente até uma casa específica (com salário se passar pelo Início). */
export function moveForwardTo(ctx: Ctx, player: PlayerId, target: number): void {
  const p = getPlayer(ctx.s, player);
  const steps = (target - p.position + BOARD_SIZE) % BOARD_SIZE;
  if (steps > 0) moveForward(ctx, player, steps);
}

export function teleport(ctx: Ctx, player: PlayerId, target: number, how: 'teleport' | 'back' = 'teleport'): void {
  const p = getPlayer(ctx.s, player);
  const from = p.position;
  p.position = target;
  emit(ctx, { type: 'moved', player, from, to: target, how });
}

/** Manda para a Ilha. */
export function sendToJail(ctx: Ctx, player: PlayerId, reason: string): void {
  const s = ctx.s;
  const p = getPlayer(s, player);
  teleport(ctx, player, ISLAND_SQUARE);
  p.inJail = true;
  p.jailAttempts = 0;
  if (reason === 'sonegacao') p.evader = true;
  else delete p.evader;
  p.worldTour = false;
  // Ir para a Ilha encerra o movimento: some o que ainda faltava resolver para este jogador.
  s.queue = s.queue.filter((st) => st.player !== player);
  if (s.players[s.current]!.id === player) {
    s.extraRoll = false;
    s.doublesInRow = 0;
  }
  emit(ctx, { type: 'sentToJail', player, reason });
}

export function leaveJail(ctx: Ctx, player: PlayerId, how: 'paid' | 'card' | 'doubles' | 'forced'): void {
  const p = getPlayer(ctx.s, player);
  p.inJail = false;
  p.jailAttempts = 0;
  delete p.evader;
  emit(ctx, { type: 'leftJail', player, how });
}

/** Passa a propriedade para o jogador (mantendo o nível) e avisa se ele fechou o grupo. */
export function setOwner(ctx: Ctx, player: PlayerId, index: number, level?: Level): void {
  if (!isOwnable(index)) throw new Error(`Casa não comprável: ${index}`);
  const prev = ctx.s.props[index];
  const hadGroup = ownsFullGroup(ctx.s, player, index);
  ctx.s.props[index] = { owner: player, level: level ?? prev?.level ?? 0 };
  touch(ctx.s, index);
  const sq = square(index);
  if (!hadGroup && sq.kind === 'city' && sq.group && ownsFullGroup(ctx.s, player, index)) {
    emit(ctx, { type: 'groupCompleted', player, group: sq.group });
  }
}

/** Marca que a cidade teve movimento nesta rodada (o MST só invade cidade parada). */
export function touch(s: GameState, index: number): void {
  (s.lastVisit ??= {})[index] = s.round;
}

/** Vale até o fim da próxima rodada completa (`rounds` rodadas a partir de agora). */
export function untilAfterRounds(s: GameState, rounds: number): number {
  return s.turnCounter + rounds * turnsPerRound(s) + 1;
}

export function addEffect(ctx: Ctx, effect: Effect): void {
  ctx.s.effects.push(effect);
  emit(ctx, { type: 'effectStarted', effect });
}

export function discardCard(ctx: Ctx, card: string): void {
  ctx.s.discard.push(card);
}

/** Cartas na mão: as guardadas do baralho mais as compradas na lojinha. */
export function handSize(p: Pick<Player, 'held' | 'shop'>): number {
  return p.held.length + (p.shop?.length ?? 0);
}

/** Tem esta carta (do baralho ou da lojinha)? */
export function hasCard(p: Pick<Player, 'held' | 'shop'>, card: HeldCard): boolean {
  return p.held.includes(card) || (p.shop ?? []).includes(card);
}

/** Guarda a carta na mão; com a mão cheia (3), a carta volta ao baralho sem efeito. */
export function keepCard(ctx: Ctx, player: PlayerId, card: HeldCard): void {
  const p = getPlayer(ctx.s, player);
  if (handSize(p) < HAND_LIMIT) {
    p.held.push(card);
    emit(ctx, { type: 'cardKept', player, card });
  } else {
    discardCard(ctx, card);
    emit(ctx, { type: 'cardDiscardedHandFull', player, card });
  }
}

/** Gasta uma carta guardada (ela volta ao descarte). Retorna false se o jogador não a tem. */
export function useHeld(ctx: Ctx, player: PlayerId, card: HeldCard): boolean {
  const p = getPlayer(ctx.s, player);
  const i = p.held.indexOf(card);
  if (i >= 0) {
    p.held.splice(i, 1);
    discardCard(ctx, card);
  } else {
    // Comprada na lojinha: não volta ao baralho.
    const j = (p.shop ?? []).indexOf(card);
    if (j < 0) return false;
    p.shop!.splice(j, 1);
  }
  emit(ctx, { type: 'cardUsed', player, card });
  return true;
}

/** Derruba uma casa (casa 1 a casa 3 baixa um nível; hotel e bandeirola não). */
export function destroyHouse(ctx: Ctx, index: number, by: PlayerId | null, card: string): void {
  const o = ctx.s.props[index];
  if (!o || o.level < 1 || o.level > 3) throw new Error(`Não há casa destrutível em ${index}`);
  o.level = (o.level - 1) as Level;
  emit(ctx, { type: 'houseDestroyed', owner: o.owner, square: index, by, card });
}

/** Cidades do jogador com casa 1 a casa 3 (hotel nunca é destruído). */
export function destructibleSquares(s: GameState, owner: PlayerId): number[] {
  return CITY_SQUARES.filter((i) => {
    const o = s.props[i];
    return !!o && o.owner === owner && o.level >= 1 && o.level <= 3;
  });
}
